import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

/**
 * Componente para renderizar contenido Markdown de forma segura
 *
 * Características:
 * - Soporte para GitHub Flavored Markdown (GFM): tablas, tachado, listas de tareas
 * - Bloques de código con resaltado de sintaxis
 * - Listas ordenadas y no ordenadas
 * - Enlaces y referencias
 * - Seguridad: sanitized input por defecto (no se permite HTML arbitrario)
 *
 * Estilos: Utiliza clases de Tailwind CSS para consistencia con la aplicación
 */
const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  className = ''
}) => {
  return (
    <div className={`markdown-content ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Párrafos
          p: ({ children }) => (
            <p className="mb-3 last:mb-0 text-ink leading-relaxed">
              {children}
            </p>
          ),

          // Títulos
          h1: ({ children }) => (
            <h1 className="text-2xl font-display font-bold text-ink mb-4 mt-6 first:mt-0">
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-xl font-display font-bold text-ink mb-3 mt-5 first:mt-0">
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-lg font-display font-semibold text-ink mb-2 mt-4 first:mt-0">
              {children}
            </h3>
          ),
          h4: ({ children }) => (
            <h4 className="text-base font-display font-semibold text-ink mb-2 mt-3 first:mt-0">
              {children}
            </h4>
          ),

          // Listas
          ul: ({ children }) => (
            <ul className="list-disc list-inside mb-3 space-y-1 text-ink">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal list-inside mb-3 space-y-1 text-ink">
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li className="ml-4">{children}</li>
          ),

          // Bloques de código
          code: ({ className, children, ...props }: React.HTMLAttributes<HTMLElement> & { className?: string }) => {
            const isInline = !className?.includes('language-');
            if (isInline) {
              return (
                <code className="bg-paper-2 text-forest-mid px-1.5 py-0.5 rounded text-sm font-mono" {...props}>
                  {children}
                </code>
              );
            }
            return (
              <code className={className} {...props}>
                {children}
              </code>
            );
          },
          pre: ({ children }) => (
            <pre className="bg-ink text-paper p-4 rounded-lg overflow-x-auto mb-4 text-sm font-mono">
              {children}
            </pre>
          ),

          // Enlaces
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-forest hover:underline"
            >
              {children}
            </a>
          ),

          // Imágenes (fotos de referencia de plantas y ambientes, con su crédito debajo)
          img: ({ src, alt }) => (
            <img
              src={src}
              alt={alt ?? ''}
              loading="lazy"
              className="my-2 max-h-72 w-auto max-w-full rounded-lg border border-hairline bg-paper-2 object-contain"
            />
          ),

          // Bloques de cita
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-hairline pl-4 py-1 my-3 italic text-sage bg-paper-2/60">
              {children}
            </blockquote>
          ),

          // Texto fuerte y énfasis
          strong: ({ children }) => (
            <strong className="font-bold text-ink">
              {children}
            </strong>
          ),
          em: ({ children }) => (
            <em className="italic text-ink">
              {children}
            </em>
          ),

          // Tablas (GFM)
          table: ({ children }) => (
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full divide-y divide-hairline border border-hairline">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-paper-2">
              {children}
            </thead>
          ),
          tbody: ({ children }) => (
            <tbody className="bg-paper divide-y divide-hairline">
              {children}
            </tbody>
          ),
          tr: ({ children }) => (
            <tr>{children}</tr>
          ),
          th: ({ children }) => (
            <th className="px-4 py-2 text-left text-xs font-medium text-sage uppercase tracking-wider">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="px-4 py-2 text-sm text-ink">
              {children}
            </td>
          ),

          // Separador horizontal
          hr: () => (
            <hr className="my-4 border-hairline" />
          ),

          // Tachado (GFM)
          del: ({ children }) => (
            <del className="line-through text-sage">
              {children}
            </del>
          ),
        }}
      >
        {content}
      </ReactMarkdown>

      <style>{`
        .markdown-content code {
          font-family: 'Courier New', Courier, monospace;
        }
      `}</style>
    </div>
  );
};

export default MarkdownRenderer;
